import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import Session from 'supertokens-web-js/recipe/session'
import EmailPassword from 'supertokens-web-js/recipe/emailpassword'
import type { AuthUser } from '@/types'
import { ADMIN_ME_QUERY } from '@/graphql/queries/users'
import { client } from '@/plugins/urql'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const isLoading = ref(false)
  const isAuthenticated = computed(() => !!user.value)
  const hasCheckedSession = ref(false)
  let sessionCheckPromise: Promise<void> | null = null

  const checkSession = async () => {
    // If a session check is already in progress, wait for it to complete
    if (sessionCheckPromise) {
      console.log('Session check already in progress, waiting for completion...')
      return sessionCheckPromise
    }

    // If session has already been checked, no need to check again
    if (hasCheckedSession.value) {
      console.log('Session already checked, skipping...')
      return Promise.resolve()
    }

    // Create a new session check promise
    sessionCheckPromise = (async () => {
      try {
        isLoading.value = true
        console.log('Starting session check...')

        // Check if SuperTokens session exists
        const sessionExists = await Session.doesSessionExist()
        console.log('SuperTokens session exists:', sessionExists)

        if (sessionExists) {
          // Try to get current admin user info using session
          console.log('Fetching admin user data...')

          // Add retry logic for GraphQL query
          let result
          let retries = 3

          while (retries > 0) {
            try {
              result = await client.query(
                ADMIN_ME_QUERY,
                {},
                {
                  requestPolicy: 'network-only',
                  fetchOptions: {
                    credentials: 'include' as RequestCredentials,
                    headers: {
                      'broker-id': 'bostello',
                      'Content-Type': 'application/json',
                    },
                  },
                }
              )

              if (result.data || result.error) {
                break
              }
            } catch (queryError) {
              console.warn(`GraphQL query attempt failed, retries left: ${retries - 1}`, queryError)
              retries--
              if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 500))
              }
            }
          }

          console.log('Admin me query result:', result)

          if (result?.data?.adminMe && !result.error) {
            const adminUser = result.data.adminMe
            user.value = {
              id: adminUser.id,
              email: adminUser.email,
              firstName: adminUser.firstName || undefined,
              lastName: adminUser.lastName || undefined,
              roles: adminUser.roles,
              agencyId: adminUser.agencyId ?? undefined,
              permissions: [], // Permissions would be derived from roles in a real implementation
            }
            console.log('User authenticated:', user.value)
          } else if (result?.error) {
            console.error('GraphQL error during session check:', result.error)
            // Soft-fail: keep existing session/user; do not sign out on transient errors
            console.warn('Keeping existing auth state despite GraphQL error.')
          } else {
            console.warn(
              'Session exists but no admin user data found. Keeping existing auth state.'
            )
            // Soft-fail: keep existing user if any; backend might be temporarily unavailable
          }
        } else {
          console.log('No valid session found')
          // No valid session
          user.value = null
        }

        hasCheckedSession.value = true
      } catch (error) {
        console.error('Session check failed:', error)
        // Soft-fail: do not clear user on network/runtime errors; keep current state
        hasCheckedSession.value = true
      } finally {
        isLoading.value = false
        sessionCheckPromise = null // Clear the promise
      }
    })()

    return sessionCheckPromise
  }

  const signIn = async (email: string, password: string) => {
    try {
      isLoading.value = true
      console.log('Starting sign in process for:', email)

      // Use SuperTokens EmailPassword signin
      const response = await EmailPassword.signIn({
        formFields: [
          {
            id: 'email',
            value: email,
          },
          {
            id: 'password',
            value: password,
          },
        ],
      })

      console.log('SuperTokens signin response:', response)

      if (response.status === 'OK') {
        // Successfully signed in with SuperTokens
        // Now get user data from our GraphQL endpoint using cookies
        console.log('Fetching admin user data after signin...')
        const result = await client.query(
          ADMIN_ME_QUERY,
          {},
          {
            requestPolicy: 'network-only', // Always fetch fresh data
            fetchOptions: {
              credentials: 'include' as RequestCredentials,
              headers: {
                'broker-id': 'bostello',
                'Content-Type': 'application/json',
              },
            },
          }
        )
        console.log('Admin me query result after signin:', result)

        if (result.data?.adminMe) {
          const adminUser = result.data.adminMe
          user.value = {
            id: adminUser.id,
            email: adminUser.email,
            firstName: adminUser.firstName || undefined,
            lastName: adminUser.lastName || undefined,
            roles: adminUser.roles,
            agencyId: adminUser.agencyId ?? undefined,
            permissions: [], // Would be derived from roles in real implementation
          }

          hasCheckedSession.value = true
          console.log('Sign in successful:', user.value)
          return { success: true }
        } else {
          console.warn('SuperTokens signin succeeded but no admin user data')
          // SuperTokens signin succeeded but no admin user data
          await Session.signOut()
          return { success: false, error: 'Admin access not found' }
        }
      } else if (response.status === 'WRONG_CREDENTIALS_ERROR') {
        return { success: false, error: 'Invalid email or password' }
      } else if (response.status === 'SIGN_IN_NOT_ALLOWED') {
        return {
          success: false,
          error: 'Sign in not allowed. Please contact administrator.',
        }
      } else {
        return { success: false, error: 'Login failed. Please try again.' }
      }
    } catch (error) {
      console.error('Sign in error:', error)
      return { success: false, error: 'An error occurred during login' }
    } finally {
      isLoading.value = false
    }
  }

  const signOut = async () => {
    try {
      isLoading.value = true

      // Sign out using SuperTokens
      await Session.signOut()

      // Clear user state
      user.value = null
      // Mark session as checked to prevent immediate re-check on next navigation
      hasCheckedSession.value = true
    } catch (error: any) {
      console.error('Sign out error:', error)

      // Check if this is a timeout error (504 Gateway Time-out)
      const isTimeoutError = error?.message?.includes('504') ||
                            error?.message?.includes('Gateway Time-out') ||
                            error?.message?.includes('timeout')

      if (isTimeoutError) {
        console.warn('Sign out timed out - session may still be active on server')
        // For timeout errors, don't clear user state immediately
        // The session might still be active on the server
        // We'll still mark as checked to prevent re-check loops
        hasCheckedSession.value = true
        // Return error info so caller can handle appropriately
        throw new Error('Sign out timed out. Please check if you are fully signed out.')
      } else {
        // For other errors, clear user state as before
        // This maintains backward compatibility for other types of errors
        user.value = null
        hasCheckedSession.value = true
      }
    } finally {
      isLoading.value = false
    }
  }

  const forceSignOut = async () => {
    try {
      isLoading.value = true

      // Try to sign out on server, but don't fail if it times out
      try {
        await Session.signOut()
      } catch (error) {
        console.warn('Server sign out failed, but proceeding with local sign out:', error)
      }

      // Always clear user state locally
      user.value = null
      hasCheckedSession.value = true
    } catch (error) {
      console.error('Force sign out error:', error)
      // Even if there's an error, ensure local state is cleared
      user.value = null
      hasCheckedSession.value = true
    } finally {
      isLoading.value = false
    }
  }

  const hasPermission = (permission: string): boolean => {
    return user.value?.permissions?.includes(permission) ?? false
  }

  const hasRole = (role: string): boolean => {
    return user.value?.roles?.includes(role) ?? false
  }

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some(role => hasRole(role))
  }

  const sendPasswordResetEmail = async (email: string) => {
    try {
      // Use explicit REST call with rid header to ensure emailpassword recipe is used
      const res = await fetch(`/auth/user/password/reset/token`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          rid: 'emailpassword',
        },
        body: JSON.stringify({
          formFields: [{ id: 'email', value: email }],
        }),
      })

      if (!res.ok) {
        const msg = await res.text().catch(() => '')
        return { success: false, error: msg || `Failed with ${res.status}` }
      }
      const data = await res.json().catch(() => ({}))
      if (data.status === 'OK' || data.success === true) {
        return { success: true }
      }
      return { success: false, error: data?.message || 'Failed to send reset email' }
    } catch (error) {
      console.error('Password reset error:', error)
      return {
        success: false,
        error: 'An error occurred while sending password reset email',
      }
    }
  }

  const submitNewPassword = async (formFields: { id: string; value: string }[], token?: string) => {
    try {
      const response = await EmailPassword.submitNewPassword({
        formFields,
        token,
      })

      if (response.status === 'OK') {
        return { success: true }
      } else if (response.status === 'RESET_PASSWORD_INVALID_TOKEN_ERROR') {
        return { success: false, error: 'Invalid or expired reset token' }
      } else if (response.status === 'FIELD_ERROR') {
        const fieldErrors = response.formFields.map(field => field.error).join(', ')
        return { success: false, error: fieldErrors }
      } else {
        return { success: false, error: 'Failed to reset password' }
      }
    } catch (error) {
      console.error('Password reset submit error:', error)
      return {
        success: false,
        error: 'An error occurred while resetting password',
      }
    }
  }

  return {
    user: readonly(user),
    isLoading: readonly(isLoading),
    isAuthenticated,
    hasCheckedSession: readonly(hasCheckedSession),
    checkSession,
    signIn,
    signOut,
    forceSignOut,
    hasPermission,
    hasRole,
    hasAnyRole,
    sendPasswordResetEmail,
    submitNewPassword,
  }
})
